/**
 * [C35] Documents (B5 part 2): the print templates the PDF worker renders (D-146), the checks on the props a client
 * sends with a PDF request, the WhatsApp phone form (D-150) and the per-voucher format defaults (D-149).
 * Leaf module: shared by the API, the worker and the web app.
 */
import { z } from "zod";

/** Must equal the keys of `PRINT_TEMPLATES` in `@faylasuf/print` (asserted by the worker's test). */
export const PRINT_TEMPLATE_NAMES = [
  "statement_a4",
  "customer_entry_a4",
  "transfer_voucher_a4",
  "sales_invoice_a4",
  "installment_contract_a4",
  "installment_list_a4",
  "installment_receipt_a4",
  "voucher_80mm",
] as const;
export type PrintTemplateKey = (typeof PRINT_TEMPLATE_NAMES)[number];

/** D-149: the four single-operation vouchers that may print on 80 mm (statements, contracts, reports, lists: A4 only). */
export const VOUCHER_TYPES = ["customer_entry", "transfer_voucher", "installment_receipt", "sales_invoice"] as const;
export type VoucherType = (typeof VOUCHER_TYPES)[number];
export const voucherFormatsSchema = z
  .object({ customer_entry: z.enum(["a4", "80mm"]), transfer_voucher: z.enum(["a4", "80mm"]), installment_receipt: z.enum(["a4", "80mm"]), sales_invoice: z.enum(["a4", "80mm"]) })
  .strict();
export type VoucherFormats = z.infer<typeof voucherFormatsSchema>;

export const DOCUMENT_LIMITS = { bytes: 2_000_000, depth: 8, nodes: 40_000, stringLength: 20_000, arrayLength: 5_000, logoBytes: 300_000 } as const;

const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype", "dangerouslySetInnerHTML"]);
/** React-special props: refused on the template itself (nested objects such as a column's `key` are data). */
const FORBIDDEN_TOP = new Set(["children", "ref", "key", "style", "className"]);
const LOGO_RE = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * Structural check of a PDF request's props (the API before forwarding and the worker before rendering): plain JSON
 * (objects, arrays, strings, finite numbers, booleans, null), bounded depth / size, no React-special or prototype keys,
 * an image URL only as a small data URI (the worker's browser has no network). Returns the first problem or null.
 */
export function checkDocumentProps(props: unknown): string | null {
  if (typeof props !== "object" || props === null || Array.isArray(props)) return "PROPS_NOT_OBJECT";
  let nodes = 0;
  const walk = (v: unknown, depth: number, key: string): string | null => {
    if (++nodes > DOCUMENT_LIMITS.nodes) return "PROPS_TOO_LARGE";
    if (depth > DOCUMENT_LIMITS.depth) return "PROPS_TOO_DEEP";
    if (v === null || typeof v === "boolean") return null;
    if (typeof v === "number") return Number.isFinite(v) ? null : "PROPS_NUMBER";
    if (typeof v === "string") {
      if (key === "logoUrl") return v.length <= DOCUMENT_LIMITS.logoBytes * 1.4 && LOGO_RE.test(v) ? null : "PROPS_LOGO";
      return v.length <= DOCUMENT_LIMITS.stringLength ? null : "PROPS_STRING_TOO_LONG";
    }
    if (Array.isArray(v)) {
      if (v.length > DOCUMENT_LIMITS.arrayLength) return "PROPS_ARRAY_TOO_LONG";
      for (const x of v) {
        const e = walk(x, depth + 1, key);
        if (e) return e;
      }
      return null;
    }
    if (typeof v === "object") {
      if (Object.getPrototypeOf(v) !== Object.prototype && Object.getPrototypeOf(v) !== null) return "PROPS_NOT_PLAIN";
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
        if (FORBIDDEN_KEYS.has(k) || (depth === 0 && (FORBIDDEN_TOP.has(k) || /^on[A-Z]/.test(k)))) return "PROPS_FORBIDDEN_KEY";
        const e = walk(x, depth + 1, k);
        if (e) return e;
      }
      return null;
    }
    return "PROPS_TYPE";
  };
  return walk(props, 0, "");
}

export const documentPdfSchema = z
  .object({
    template: z.enum(PRINT_TEMPLATE_NAMES),
    props: z.record(z.string(), z.unknown()),
    /** File name without extension; letters, digits, dash, underscore, dot. */
    fileName: z.string().trim().regex(/^[\p{L}\p{N}_.-]{1,80}$/u).optional(),
    /** The issued document this PDF is a copy of (D-147), when the section's print route issued one. */
    docType: z.string().max(40).optional(),
    number: z.string().max(40).optional(),
    operationId: z.string().uuid().optional(),
    accountId: z.string().uuid().optional(),
  })
  .strict();
export type DocumentPdfRequest = z.infer<typeof documentPdfSchema>;

/**
 * D-150: an account's stored phone in the international form WhatsApp needs (old links used the local "07…" form and
 * failed, F-221). Iraqi mobiles: 07XXXXXXXXX / 7XXXXXXXXX / +9647… / 009647… → 9647XXXXXXXXX. Any other number with
 * a country code (+, 00) keeps it. Returns null when nothing usable is left (the link then opens without a recipient).
 */
export function whatsappPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  let d = trimmed.replace(/[^\d]/g, "");
  if (!d) return null;
  if (trimmed.startsWith("+")) return d.length >= 8 && d.length <= 15 ? d : null;
  if (d.startsWith("00")) d = d.slice(2);
  else if (/^07\d{9}$/.test(d)) d = `964${d.slice(1)}`;
  else if (/^7\d{9}$/.test(d)) d = `964${d}`;
  else if (/^9640\d{10}$/.test(d)) d = `964${d.slice(4)}`;
  return d.length >= 8 && d.length <= 15 ? d : null;
}

/** [C35] D-148: words of amounts the client already shows (statements, the dual reconciliation, the sales total). */
export const documentWordsSchema = z
  .object({
    locale: z.enum(["ar", "en"]).default("ar"),
    items: z
      .array(
        z
          .object({
            lines: z.array(z.object({ amount: z.string().regex(/^-?\d{1,15}(\.\d{1,6})?$/), currency: z.string().regex(/^[A-Z]{3,5}$/) }).strict()).min(1).max(6),
            side: z.enum(["lakum", "alaykum"]).nullable().optional(),
          })
          .strict(),
      )
      .min(1)
      .max(40),
  })
  .strict();
export type DocumentWordsRequest = z.infer<typeof documentWordsSchema>;
